import React, { useEffect, useState } from 'react';
import './DisbursementsAndProjectsForTheYear.scss';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { formatMillionNumber } from '../../../services/numberUtil';
import { LinearProgress, Tooltip, withStyles, CircularProgress } from '@material-ui/core';
import NumberFormat from 'react-number-format';

type ValueHeader = {
  advanceOnCurrent: number;
  advanceOnOriginal: number;
  percentBar: number;
};

export const DisbursementsAndProjectsForTheYearHeader = () => {
  const [valueHeaderIndicador, setValueHeaderIndicador] = useState<ValueHeader>();
  const indicatorProjects = useSelector<any, any[]>(state => state.indicatorProjects);
  const { currentIndicator } = useSelector<any, any>(state => state);

  const { t } = useTranslation();

  const loadingRedux = useSelector<any, boolean>(state => state.dataTableInfo.loading);

  const dataForHeader = currentIndicator?.data ?? {};

  const BlackTooltip = withStyles({
    tooltip: {
      backgroundColor: '#101319',
      fontSize: '16px',
      fontFamily: 'Rubik',
      lineHeight: '20px',
      padding: '8px 12px',
      borderRadius: '8px',
    },
    arrow: {
      fontSize: 20,
      color: '#4A4A4A',
      '&::before': {
        backgroundColor: '#101319',
      },
    },
  })(Tooltip);

  useEffect(() => {
    if (dataForHeader && dataForHeader.current_project && dataForHeader.original_project) {
      setValueHeaderIndicador({
        advanceOnCurrent: ((dataForHeader.disbursed ?? 0) / (dataForHeader.current_project || 1)) * 100,
        advanceOnOriginal: ((dataForHeader.disbursed ?? 0) / (dataForHeader.original_project || 1)) * 100,
        percentBar: ((dataForHeader.disbursed ?? 0) * 100) / (dataForHeader.current_project || 1),
      });
    }
  }, [dataForHeader, indicatorProjects]);

  if (loadingRedux) {
    return (
      <section className="container-summary loading-container">
        <div className="loading-content">
          <CircularProgress color="primary" size={36} />
        </div>
      </section>
    );
  }

  return (
    <section className="container-summary">
      <section className="container-summary-left">
        <p className="summary-title">
          <b>{t('new_summary')} </b> <span>({t('new_annual_cumulative')})</span>
        </p>
        <div className="container-data-left">
          <div className="original-container">
            <p className="data-show">
              <NumberFormat
                value={formatMillionNumber(dataForHeader?.original_project ?? 0)}
                displayType={'text'}
                thousandSeparator={'.'}
                decimalSeparator={','}
                decimalScale={1}
              />{' '}
              {dataForHeader?.currency_project ?? 'MM'}
            </p>
            <span>
              {t('new_orginila_projection')} <b>(A)</b>
            </span>
          </div>
          <div className="current-container">
            <p className="data-show">
              <NumberFormat
                value={formatMillionNumber(dataForHeader?.current_project ?? 0)}
                displayType={'text'}
                thousandSeparator={'.'}
                decimalSeparator={','}
                decimalScale={1}
              />{' '}
              {dataForHeader?.currency_project ?? 'MM'}
            </p>
            <span>
              {t('new_current_projection')} <b>(B)</b>
            </span>
          </div>
          <div className="detour-container">
            <p className="data-show-detour">
              <NumberFormat
                value={formatMillionNumber(
                  dataForHeader?.current_project && dataForHeader?.original_project
                    ? dataForHeader?.current_project - dataForHeader?.original_project
                    : 0,
                )}
                displayType={'text'}
                thousandSeparator={'.'}
                decimalSeparator={','}
                decimalScale={1}
              />{' '}
              {dataForHeader?.currency_project ?? 'MM'}{' '}
              <span className="percent-container">{Math.round(dataForHeader?.percentage ?? 0)}%</span>
            </p>
            <span>
              {t('deviation').charAt(0).toLocaleUpperCase() + t('deviation').slice(1)} <b>(B - A)</b>
            </span>
          </div>
        </div>
      </section>
      <section className="container-summary-medium">
        <div className="container-medium-line"></div>
      </section>
      <section className="container-summary-right">
        <div className="container-data-right">
          <div className="disbursed-container">
            <p className="style-value">
              <NumberFormat
                value={formatMillionNumber(dataForHeader?.disbursed ?? 0)}
                displayType={'text'}
                thousandSeparator={'.'}
                decimalSeparator={','}
                decimalScale={1}
              />{' '}
              {dataForHeader?.currency_project ?? 'MM'}
            </p>
            <span className="disbursed-label">{t('DI')}</span>
          </div>
          <div className="percent-container">
            <p className="style-value">
              <span className="style-percent-one">
                {valueHeaderIndicador?.advanceOnCurrent && Math.round(valueHeaderIndicador?.advanceOnCurrent)}%
              </span>{' '}
              {t('new_advance_on_current')}
            </p>
            <p className="style-value">
              <span className="style-percent-two">
                {' '}
                {valueHeaderIndicador?.advanceOnOriginal && Math.round(valueHeaderIndicador?.advanceOnOriginal)}%
              </span>{' '}
              {t('new_advance_on_original')}
            </p>
          </div>
          <div className="graphic-container">
            <p className="style-value-detour">{t('new_preview_of_the_current_projection')} </p>
            <BlackTooltip
              title={
                <>
                  <p>{t('new_annual_progress')}</p>
                  <p style={{ margin: '5px auto', display: 'flex', gap: '5px', alignItems: 'center' }}>
                    <span
                      style={{ display: 'inline-block', width: '25px', height: '25px', backgroundColor: '#236bff' }}
                    ></span>{' '}
                    {t('DI')}: USD {dataForHeader?.disbursed}
                  </p>
                  <p style={{ font: '15px' }}>
                    {t('new_advance_on_current')}:{' '}
                    {valueHeaderIndicador?.advanceOnCurrent && Math.round(valueHeaderIndicador?.advanceOnCurrent)}%
                  </p>
                  <p style={{ margin: '5px auto', font: '15px' }}>
                    {t('new_advance_on_original')}:{' '}
                    {valueHeaderIndicador?.advanceOnCurrent && Math.round(valueHeaderIndicador?.advanceOnOriginal)} %
                  </p>
                </>
              }
              arrow
            >
              <LinearProgress
                variant="determinate"
                value={valueHeaderIndicador?.percentBar && Math.round(valueHeaderIndicador?.percentBar)}
                style={{ width: '100%', height: '10px', borderRadius: '2px' }}
              />
            </BlackTooltip>
            <div className="data-container">
              <div className="style-disbursed">
                <NumberFormat
                  value={formatMillionNumber(dataForHeader?.disbursed ?? 0)}
                  displayType={'text'}
                  thousandSeparator={'.'}
                  decimalSeparator={','}
                  decimalScale={1}
                />{' '}
                {dataForHeader?.currency_project ?? 'MM'}
                <span className="title-avance">{t('DI')}</span>
              </div>
              <div className="style-pending">
                <NumberFormat
                  value={formatMillionNumber(dataForHeader?.pending ?? 0)}
                  displayType={'text'}
                  thousandSeparator={'.'}
                  decimalSeparator={','}
                  decimalScale={1}
                />{' '}
                {dataForHeader?.currency_project}
                <span className="title-avance">{t('new_pending_disbursement')}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

    </section>
  )
};
